import React, { useState, useEffect, useMemo } from 'react';
import { Pagination } from 'react-bootstrap';

const SAPagination = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    showGoToPage = true,
    showPageInfo = true,
    className = '',
    size = ''
}) => {
    const [goToPage, setGoToPage] = useState('');

    const handlePageClick = (pageNumber) => {
        if (pageNumber < 0 || pageNumber >= totalPages) return;
        if (pageNumber === currentPage) return;
        onPageChange(pageNumber);
    };

    const handleGoToPage = (e) => {
        e.preventDefault();
        const pageNumber = parseInt(goToPage, 10) - 1; // Convert to 0-based index
        if (!Number.isNaN(pageNumber) && pageNumber >= 0 && pageNumber < totalPages) {
            handlePageClick(pageNumber);
            setGoToPage('');
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.key === 'ArrowLeft' && currentPage > 0) {
                handlePageClick(currentPage - 1);
            } else if (e.key === 'ArrowRight' && currentPage < totalPages - 1) {
                handlePageClick(currentPage + 1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentPage, totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

    const visiblePages = useMemo(() => {
        if (!totalPages || totalPages <= 1) return [];
        const pages = new Set();
        pages.add(0);
        pages.add(totalPages - 1);
        for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
            if (i >= 0 && i < totalPages) pages.add(i);
        }
        return Array.from(pages).sort((a, b) => a - b);
    }, [currentPage, totalPages]);

    // Don't render if no pagination needed
    if (!totalPages || totalPages <= 1) {
        return null;
    }

    const startItem = currentPage * itemsPerPage + 1;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalItems);

    const pageItems = [];
    visiblePages.forEach((pageIndex, idx) => {
        const prev = visiblePages[idx - 1];
        if (prev != null && pageIndex - prev > 1) {
            pageItems.push(
                <Pagination.Ellipsis key={`ellipsis-${pageIndex}`} disabled />
            );
        }
        pageItems.push(
            <Pagination.Item
                key={pageIndex}
                active={pageIndex === currentPage}
                onClick={() => handlePageClick(pageIndex)}
            >
                {pageIndex + 1}
            </Pagination.Item>
        );
    });

    return (
        <div className={`page-footer d-flex justify-content-between align-items-center mt-3 ${className}`}>
            {showPageInfo && (
                <div className="pagination-info">
                    <span className="text-muted">
                        Showing {startItem} to {endItem} of {totalItems} results
                    </span>
                    {showGoToPage ? (
                        <div className="go-to-page mt-2">
                            <form onSubmit={handleGoToPage} className="d-flex align-items-center">
                                <label className="me-2 mb-0">Go to page:</label>
                                <input
                                    type="number"
                                    min="1"
                                    max={totalPages}
                                    value={goToPage}
                                    onChange={(e) => setGoToPage(e.target.value)}
                                    className="form-control form-control-sm"
                                    style={{ width: '80px' }}
                                    placeholder="Page #"
                                    aria-label="Jump to page number"
                                />
                                <button type="submit" className="btn btn-primary btn-sm ms-2">Go</button>
                            </form>
                        </div>
                    ) : null}
                </div>
            )}

            <Pagination className={`mb-0 ${size}`}>
                <Pagination.First
                    onClick={() => handlePageClick(0)}
                    disabled={currentPage === 0}
                />
                <Pagination.Prev
                    onClick={() => handlePageClick(currentPage - 1)}
                    disabled={currentPage === 0}
                />

                {pageItems}

                <Pagination.Next
                    onClick={() => handlePageClick(currentPage + 1)}
                    disabled={currentPage === totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => handlePageClick(totalPages - 1)}
                    disabled={currentPage === totalPages - 1}
                />
            </Pagination>
        </div>
    );
};

export default SAPagination;
